<template>
  <div class="agent-chat-window" ref="chatContainer">
    <!-- 欢迎卡片：无消息时显示 -->
    <div v-if="showWelcome" class="agent-welcome">
      <div class="agent-welcome__card">
        <p class="agent-welcome__intro">我是您的 AI 医生助手，一站式解答疾问、提供建议、助您提升工作效率，我可以帮您：</p>
        <ul class="agent-welcome__list">
          <li>根据患者信息生成健康报告</li>
          <li>根据患者特征生成健康计划</li>
          <li>生成个性化健康宣教内容</li>
        </ul>
      </div>
      <div class="agent-welcome__divider">暂无历史消息</div>
      <!-- 快捷功能入口 -->
      <div class="agent-quick-actions">
        <button
          v-for="action in quickActions"
          :key="action.key"
          class="agent-quick-btn"
          @click="$emit('quick-send', action.text)"
        >
          <span class="agent-quick-btn__icon" v-html="action.icon"></span>
          <span>{{ action.label }}</span>
        </button>
      </div>
    </div>

    <div v-else class="agent-messages-list" ref="messagesList">
      <AgentMessageBubble
        v-for="msg in messages"
        :key="msg.id"
        :role="msg.role"
        :text="msg.text"
        :done="msg.done"
        :status="msg.status"
        :progress-text="msg.progressText || ''"
        :cards="msg.cards || []"
        :attachments="msg.attachments || []"
        @retry="$emit('retry', msg.id)"
        @approval-decision="(approvalId, decision) => $emit('approval-decision', approvalId, decision)"
        @card-action="(cardId, action, value) => $emit('card-action', cardId, action, value)"
      />
      <!-- typing 动画 -->
      <div v-if="isTyping" class="agent-typing-bubble">
        <div class="agent-typing-avatar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#0052D9" opacity="0.12"/>
            <path d="M12 6C9.79 6 8 7.79 8 10s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z" fill="#0052D9"/>
          </svg>
        </div>
        <div class="agent-typing-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from '@vue/composition-api';
import AgentMessageBubble from './AgentMessageBubble.vue';

const BOTTOM_THRESHOLD = 80;

// 快捷功能入口配置
const quickActions = [
  {
    key: 'archive',
    label: '档案智问',
    text: '帮我查看当前患者的档案信息',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
  },
  {
    key: 'report',
    label: '健康报告',
    text: '帮我生成患者健康报告',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>',
  },
  {
    key: 'education',
    label: '健康宣教',
    text: '帮我生成个性化健康宣教内容',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>',
  },
  {
    key: 'plan',
    label: '健康计划',
    text: '帮我制定患者健康管理计划',
    icon: '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
  },
];

export default {
  name: 'AgentChatWindow',
  components: { AgentMessageBubble },
  props: {
    messages: {
      type: Array,
      default: () => [],
    },
    isTyping: {
      type: Boolean,
      default: false,
    },
    showWelcome: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['retry', 'quick-send', 'approval-decision', 'card-action'],
  setup(props) {
    const messagesList = ref(null);
    let isNearBottom = true;

    function checkNearBottom() {
      const el = messagesList.value;
      if (!el) return;
      isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < BOTTOM_THRESHOLD;
    }

    function scrollToBottom(smooth = false) {
      if (!isNearBottom) return;
      nextTick(() => {
        const el = messagesList.value;
        if (!el) return;
        el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
      });
    }

    function onScroll() {
      checkNearBottom();
    }

    onMounted(() => {
      messagesList.value && messagesList.value.addEventListener('scroll', onScroll, { passive: true });
    });

    onBeforeUnmount(() => {
      messagesList.value && messagesList.value.removeEventListener('scroll', onScroll);
    });

    // 新消息时滚动
    watch(() => props.messages.length, () => scrollToBottom(true));

    // 流式内容更新时滚动
    watch(
      () => props.messages.map((m) => (m.text || '') + '|' + (m.progressText || '')).join('\n'),
      () => scrollToBottom(),
    );

    // typing 状态变化
    watch(() => props.isTyping, () => scrollToBottom(true));

    return { messagesList, quickActions };
  },
};
</script>

<style scoped>
.agent-chat-window {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}
.agent-messages-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px 0 8px;
  scroll-behavior: smooth;
}
.agent-messages-list::-webkit-scrollbar {
  width: 4px;
}
.agent-messages-list::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 2px;
}
.agent-messages-list::-webkit-scrollbar-track {
  background: transparent;
}

/* 欢迎卡片 */
.agent-welcome {
  flex: 1;
  overflow-y: auto;
  padding: 16px 14px 12px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.agent-welcome__card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 18px;
  border: 1px solid #eef0f3;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}
.agent-welcome__intro {
  font-size: 14px;
  line-height: 1.7;
  color: #1a202c;
  margin: 0 0 12px;
}
.agent-welcome__list {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.agent-welcome__list li {
  font-size: 14px;
  line-height: 1.6;
  color: #4a5568;
}
.agent-welcome__divider {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #c0c8d4;
  gap: 10px;
}
.agent-welcome__divider::before,
.agent-welcome__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e8ecf0;
}
/* 快捷功能入口 */
.agent-quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.agent-quick-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 1.5px solid #d8e3f5;
  border-radius: 999px;
  background: #fff;
  color: #0052D9;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}
.agent-quick-btn:hover {
  background: #e8f0fe;
  border-color: #0052D9;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 82, 217, 0.15);
}
.agent-quick-btn__icon {
  display: flex;
  align-items: center;
  color: #0052D9;
}

/* typing 动画气泡 */
.agent-typing-bubble {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 10px 16px;
}
.agent-typing-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f0f4ff;
  border: 1px solid #dce6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.agent-typing-dots {
  display: inline-flex;
  gap: 5px;
  align-items: center;
  background: #fff;
  padding: 12px 16px;
  border-radius: 4px 16px 16px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f2f5;
}
.agent-typing-dots span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #86909c;
  animation: agent-typing-dot 1.4s infinite ease-in-out both;
}
.agent-typing-dots span:nth-child(1) { animation-delay: -0.32s; }
.agent-typing-dots span:nth-child(2) { animation-delay: -0.16s; }
@keyframes agent-typing-dot {
  0%, 80%, 100% { transform: scale(0.5); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
}
</style>
